import React, { useState, useContext, useEffect } from "react";

import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";

import { captureJobReport } from "../../../../utils/business";
import { updateDispatchStage } from "../../../../services/api/dispatch";

import { ModalWrapper } from "../../../../components/ui";
import { Button } from "../../../../components/ui";

import { formatDate } from "../../../../utils/datetime";

/**
 * Confirm daily review checks, persist "Reviewed", then capture job-report PDFs.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen Whether the modal is visible.
 * @param {() => void} props.onClose Close without reviewing.
 * @param {Date} props.date Dispatch day being reviewed.
 * @returns {JSX.Element}
 */
const ReviewModal = ({ isOpen, onClose, date }) => {
  const { weekStart, jobs, scopes } = useContext(WeeklyContext);
  const {
    reloadDays,
    setJobReportDate,
    setIsJobReportCapturing,
    runPlanningOperation,
  } =
    useContext(DispatchContext);
  const [accidentsReported, setAccidentsReported] = useState(false);
  const [changeOrdersIssued, setChangeOrdersIssued] = useState(false);
  const [timeMaterialReported, setTimeMaterialReported] = useState(false);
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  useEffect(() => {
    setIsConfirmDisabled(
      !(accidentsReported && changeOrdersIssued && timeMaterialReported)
    );
  }, [accidentsReported, changeOrdersIssued, timeMaterialReported]);

  /**
   * Reset confirmation checkboxes and dismiss the modal.
   *
   * @param {void}
   * @returns {void}
   */
  const handleClose = () => {
    setAccidentsReported(false);
    setChangeOrdersIssued(false);
    setTimeMaterialReported(false);
    onClose();
  };

  /**
   * Check all three review confirmations so Confirm can proceed.
   *
   * @param {void}
   * @returns {void}
   */
  const handleConfirmAll = () => {
    setAccidentsReported(true);
    setChangeOrdersIssued(true);
    setTimeMaterialReported(true);
  };

  /**
   * Persist the Reviewed stage, then generate/email job-report captures for the day.
   *
   * @param {void}
   * @returns {Promise<void>}
   */
  const handleCapture = async () => {
    setIsConfirmLoading(true);
    window.__rsappJobReportCapture = null;
    const checks = {
      accidentsReported,
      changeOrdersIssued,
      timeMaterialReported,
    };
    try {
      await runPlanningOperation({
        module: "dispatch",
        operation: "markDayReviewed",
        resources: ["dispatch.dispatch_stage", "dispatch.day_details"],
        apiCall: () => updateDispatchStage("Reviewed", date, checks),
      });
      setIsJobReportCapturing(true);
      setJobReportDate(date);
      handleClose();
      reloadDays();
      setIsConfirmLoading(false);
      await captureJobReport(
        setIsJobReportCapturing,
        weekStart,
        date,
        jobs,
        scopes
      );
    } catch (error) {
      window.__rsappJobReportCapture = {
        ok: false,
        error: error?.message || String(error),
      };
      console.error(error);
    }
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={handleClose}>
      <div className="flex flex-col items-center">
        <h2 className="text-lg font-bold text-center w-full px-8 mr-6">
          Daily Review Summary for {formatDate(date, "month DD, YYYY")}
        </h2>
        <p className="text-center mt-4">
          Daily job reports will be generated for all jobs with scopes on this
          day and emailed to the job report email group. A PDF record of each
          job report will also be uploaded to the RSCMINC file server.
        </p>
      </div>
      <span className="ml-4">Please confirm:</span>
      <div className="p-2 pl-8">
        <div className="mb-2">
          <label className="cursor-pointer">
            <input
              type="checkbox"
              checked={accidentsReported}
              onChange={() => setAccidentsReported(!accidentsReported)}
              className="mr-2 cursor-pointer"
            />
            All accidents have been reported.
          </label>
        </div>
        <div className="mb-2">
          <label className="cursor-pointer">
            <input
              type="checkbox"
              checked={changeOrdersIssued}
              onChange={() => setChangeOrdersIssued(!changeOrdersIssued)}
              className="mr-2 cursor-pointer"
            />
            All change orders have been issued.
          </label>
        </div>
        <div className="mb-2">
          <label className="cursor-pointer">
            <input
              type="checkbox"
              checked={timeMaterialReported}
              onChange={() => setTimeMaterialReported(!timeMaterialReported)}
              className="mr-2 cursor-pointer"
            />
            All time and material work has been reported.
          </label>
        </div>
        <button
          onClick={handleConfirmAll}
          className="text-blue-950 hover:text-blue-600 text-sm mb-2"
        >
          Confirm All
        </button>
      </div>
      <div className="flex flex-row justify-between">
        <Button
          data-daily-review-confirm="true"
          onClick={handleCapture}
          icon={isConfirmLoading ? "spinner" : ""}
          text={isConfirmLoading ? "" : "Confirm"}
          variant={`${isConfirmDisabled || isConfirmLoading ? "disabled" : "secondary"}`}
          className="w-full mr-1"
          disabled={isConfirmDisabled || isConfirmLoading}
        />
        <Button
          onClick={onClose}
          text="Cancel"
          variant="warning"
          className="w-full ml-1"
        />
      </div>
    </ModalWrapper>
  );
};

export default ReviewModal;
